import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { COURSES, PRICING } from "@/data/courses";

const EMPTY = { name: "", email: "", phone: "", course: "", message: "" };

export const EnrollSection = ({ preselect, onConsumePreselect }) => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (preselect) {
      setForm((f) => ({ ...f, course: preselect }));
      onConsumePreselect?.();
    }
  }, [preselect, onConsumePreselect]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.course) {
      toast.error("Please fill in your name, email, and choose a program.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enrollments", form);
      setDone(true);
      setForm(EMPTY);
      toast.success("Application received. We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="enroll"
      data-testid="enroll-section"
      className="relative bg-[#0a0a0a] px-6 md:px-12 py-28 md:py-40 border-t border-white/10"
    >
      <div className="mx-auto grid max-w-[1600px] gap-16 md:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <span className="text-xs uppercase tracking-[0.4em] text-[#7C3AED]">Enrollment</span>
          <h2 className="mt-6 font-display text-6xl md:text-8xl uppercase leading-[0.9] tracking-tight text-white">
            Apply to
            <br />
            Join
          </h2>
          <p className="mt-8 max-w-md text-lg text-white/60 leading-relaxed">
            Tell us who you are and what you want to master. Admissions reviews
            every application personally — no bots, no auto-replies.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            {COURSES.map((c) => (
              <span
                key={c.key}
                className="rounded-full border border-white/15 px-4 py-1.5 text-xs uppercase tracking-widest text-white/50"
              >
                {c.key}
              </span>
            ))}
            <span className="rounded-full border border-white/15 px-4 py-1.5 text-xs uppercase tracking-widest text-white/50">
              Math Tutoring
            </span>
          </div>
          <div data-testid="enroll-price-list" className="mt-8 space-y-2.5">
            {Object.entries(PRICING).map(([k, p]) => (
              <div
                key={k}
                data-testid={`enroll-price-${k.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex flex-wrap items-baseline gap-x-3 text-sm"
              >
                <span className="text-white/80">{k}</span>
                <span className="font-display text-lg text-[#7C3AED]">{p.price}</span>
                <span className="text-xs text-white/40">{p.note}</span>
              </div>
            ))}
            <p className="pt-1 text-xs text-white/40">
              Books and art are priced individually in the gallery.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.15 }}
        >
          {done ? (
            <div
              data-testid="enroll-success"
              className="flex h-full min-h-[320px] flex-col items-start justify-center rounded-2xl border border-white/10 bg-black/40 p-10"
            >
              <div className="font-display text-4xl uppercase text-white">Thank you.</div>
              <p className="mt-4 text-white/60">
                Your application is in. Our admissions team will reach out by email soon.
              </p>
              <button
                data-testid="enroll-reset-btn"
                onClick={() => setDone(false)}
                className="magnetic-btn mt-8 rounded-full border border-white/30 px-6 py-2.5 text-sm uppercase tracking-wider text-white hover:bg-white hover:text-black"
              >
                Submit another
              </button>
            </div>
          ) : (
            <form data-testid="enroll-form" onSubmit={submit} className="space-y-8">
              <input
                data-testid="enroll-name"
                className="line-input"
                placeholder="Full name"
                value={form.name}
                onChange={set("name")}
              />
              <input
                data-testid="enroll-email"
                type="email"
                className="line-input"
                placeholder="Email address"
                value={form.email}
                onChange={set("email")}
              />
              <input
                data-testid="enroll-phone"
                className="line-input"
                placeholder="Phone (optional)"
                value={form.phone}
                onChange={set("phone")}
              />
              <div>
                <select
                  data-testid="enroll-course-trigger"
                  className="line-input cursor-pointer"
                  value={form.course}
                  onChange={(e) => setForm((f) => ({ ...f, course: e.target.value }))}
                >
                  <option value="" disabled className="bg-[#111] text-white/50">
                    Choose a program
                  </option>
                  {COURSES.map((c) => (
                    <option
                      key={c.key}
                      value={c.key}
                      className="bg-[#111] text-white"
                    >
                      {`${c.key}${PRICING[c.key] ? ` — ${PRICING[c.key].price}` : ""}`}
                    </option>
                  ))}
                  <option value="Math Tutoring" className="bg-[#111] text-white">
                    Math Tutoring — {PRICING["Math Tutoring"].price}/session
                  </option>
                </select>
              </div>
              <textarea
                data-testid="enroll-message"
                className="line-input resize-none"
                rows={3}
                placeholder="Tell us about your goals (optional)"
                value={form.message}
                onChange={set("message")}
              />
              <button
                data-testid="enroll-submit-btn"
                type="submit"
                disabled={submitting}
                className="magnetic-btn w-full rounded-full bg-[#7C3AED] px-8 py-4 text-sm font-semibold uppercase tracking-wider text-white hover:bg-white hover:text-black disabled:opacity-50"
              >
                {submitting ? "Sending…" : "Submit Application"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default EnrollSection;
